import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ChannelPage,
  GuildList,
  MemoryHealth,
  OperationsAction,
  OperationsActionResult,
  OperationsView,
  QueryLabMember,
  QueryLabRequest,
  QueryLabResult,
  QueryLabScope,
  ServerSettings,
  ShoBotSettings,
  StatusHistory,
  StatusReport,
} from '../../shared/api.js';
import { useSession } from '../session.js';
import { apiRequest, ApiRequestError } from './client.js';

/**
 * Query hooks.
 *
 * The polling interval is 20 seconds: inside the 15-30 second band the operator
 * contract asks for, and matched to the server's sampler so the page is never
 * asking for something newer than the process can have observed.
 *
 * `refetchIntervalInBackground` is left at its default of `false`, which is
 * TanStack Query already doing the right thing — timers do not fire while the
 * tab is in the background, so a dashboard left open in a spare tab overnight
 * stops probing the bot rather than hammering it until morning.
 *
 * A 401 anywhere signs the app out through the session context rather than being
 * retried, because retrying an expired session is only a way to make the login
 * screen take four times as long to appear.
 */

export const POLL_INTERVAL_MS = 20_000;

function shouldRetry(failureCount: number, error: unknown): boolean {
  if (error instanceof ApiRequestError) {
    // Client errors are answers, not failures. Retrying a 403 produces a second
    // 403 and a slower page.
    if (error.status >= 400 && error.status < 500) return false;
  }
  return failureCount < 2;
}

export function useStatus() {
  return useQuery({
    queryKey: ['status'],
    queryFn: async () => apiRequest<StatusReport>('/api/status'),
    refetchInterval: POLL_INTERVAL_MS,
    retry: shouldRetry,
  });
}

export function useStatusHistory(window: '24h' | '7d') {
  return useQuery({
    queryKey: ['status-history', window],
    queryFn: async () => apiRequest<StatusHistory>(`/api/status/history?window=${window}`),
    // History changes a bucket at a time; polling it as fast as current status
    // would be four times the work for the same picture.
    refetchInterval: POLL_INTERVAL_MS * 3,
    retry: shouldRetry,
  });
}

export function useGuilds() {
  return useQuery({
    queryKey: ['guilds'],
    queryFn: async () => apiRequest<GuildList>('/api/guilds'),
    refetchInterval: POLL_INTERVAL_MS * 3,
    retry: shouldRetry,
  });
}

export function useMemoryHealth(guildId: string | null) {
  return useQuery({
    queryKey: ['memory', guildId],
    enabled: guildId !== null,
    queryFn: async () => apiRequest<MemoryHealth>(`/api/guilds/${guildId ?? ''}/memory`),
    refetchInterval: POLL_INTERVAL_MS,
    retry: shouldRetry,
  });
}

export function useChannels(
  guildId: string | null,
  page: number,
  sort: string,
  direction: 'asc' | 'desc',
) {
  return useQuery({
    queryKey: ['channels', guildId, page, sort, direction],
    enabled: guildId !== null,
    queryFn: async () => {
      const params = new URLSearchParams({
        page: String(page),
        sort,
        direction,
      });
      return apiRequest<ChannelPage>(`/api/guilds/${guildId ?? ''}/channels?${params}`);
    },
    retry: shouldRetry,
  });
}

export function useOperations(guildId: string | null) {
  return useQuery({
    queryKey: ['operations', guildId],
    enabled: guildId !== null,
    queryFn: async () => apiRequest<OperationsView>(`/api/guilds/${guildId ?? ''}/operations`),
    refetchInterval: POLL_INTERVAL_MS,
    retry: shouldRetry,
  });
}

export function useServerSettings(guildId: string | null) {
  return useQuery({
    queryKey: ['server-settings', guildId],
    enabled: guildId !== null,
    queryFn: async () => apiRequest<ServerSettings>(`/api/guilds/${guildId ?? ''}/settings`),
    retry: shouldRetry,
  });
}

export function useShoBotSettings() {
  return useQuery({
    queryKey: ['sho-bot-settings'],
    queryFn: async () => apiRequest<ShoBotSettings>('/api/settings'),
    retry: shouldRetry,
  });
}

export function useReconciliationSetting(guildId: string | null) {
  const { session } = useSession();
  const queryClient = useQueryClient();
  return useMutation<{ enabled: boolean }, Error, boolean>({
    mutationFn: async (enabled) =>
      apiRequest(`/api/guilds/${guildId ?? ''}/settings/reconciliation`, {
        method: 'POST',
        body: { enabled },
        csrfToken: session?.csrfToken,
      }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['server-settings', guildId] }),
        queryClient.invalidateQueries({ queryKey: ['operations', guildId] }),
      ]);
    },
  });
}

export function useAutomaticVoiceSetting(guildId: string | null) {
  const { session } = useSession();
  const queryClient = useQueryClient();
  return useMutation<{ channelId: string | null }, Error, string | null>({
    mutationFn: async (channelId) =>
      apiRequest(`/api/guilds/${guildId ?? ''}/settings/automatic-voice`, {
        method: 'POST',
        body: { channelId },
        csrfToken: session?.csrfToken,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['server-settings', guildId] });
    },
  });
}

const ACTION_PATHS: Readonly<Record<OperationsAction, string>> = {
  reconcile: 'reconcile',
  backfill_pause: 'backfill-pause',
  backfill_resume: 'backfill-resume',
};

/**
 * One of the three reconciliation controls.
 *
 * The invalidation lives here rather than in the page, so every caller gets it.
 * All three actions change what the Operations view reports — the backfill
 * state, whether a forward pass is running — and two of them change the guild
 * list's live flags, so both are marked stale the moment the action is accepted.
 */
export function useOperationsAction(guildId: string | null) {
  const { session } = useSession();
  const queryClient = useQueryClient();
  return useMutation<OperationsActionResult, Error, OperationsAction>({
    mutationFn: async (action) =>
      apiRequest<OperationsActionResult>(
        `/api/guilds/${guildId ?? ''}/actions/${ACTION_PATHS[action]}`,
        { method: 'POST', body: {}, csrfToken: session?.csrfToken },
      ),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['operations', guildId] }),
        queryClient.invalidateQueries({ queryKey: ['guilds'] }),
      ]);
    },
  });
}

export function useMemberSearch(guildId: string | null, query: string) {
  return useQuery({
    queryKey: ['members', guildId, query],
    enabled: guildId !== null && query.trim().length > 0,
    queryFn: async () =>
      apiRequest<{ members: QueryLabMember[] }>(
        `/api/query-lab/guilds/${guildId ?? ''}/members?query=${encodeURIComponent(query.trim())}`,
      ),
    // The search term is a member name. It is not cached beyond the tab's
    // lifetime and never persisted.
    gcTime: 0,
    retry: shouldRetry,
  });
}

export function useMemberScope(guildId: string | null, userId: string | null) {
  return useQuery({
    queryKey: ['member-scope', guildId, userId],
    enabled: guildId !== null && userId !== null,
    queryFn: async () =>
      apiRequest<QueryLabScope>(
        `/api/query-lab/guilds/${guildId ?? ''}/members/${userId ?? ''}/channels`,
      ),
    gcTime: 0,
    retry: shouldRetry,
  });
}

export function useBudgetPosture(guildId: string | null) {
  return useQuery({
    queryKey: ['budget', guildId],
    enabled: guildId !== null,
    queryFn: async () =>
      apiRequest<MemoryHealth['budget'] & { guildId: string }>(
        `/api/query-lab/guilds/${guildId ?? ''}/budget`,
      ),
    refetchInterval: POLL_INTERVAL_MS * 3,
    retry: shouldRetry,
  });
}

/**
 * The diagnostic query.
 *
 * A mutation rather than a query, and deliberately so: the paid modes spend
 * money, and a `useQuery` would refetch them on focus, on reconnect, and on
 * every poll tick. The *result* is never cached — it lives in the calling
 * component's state and is dropped when the context changes.
 *
 * What is invalidated is everything the query may have changed. A `hybrid` or
 * `answer` run reserves and settles against the same monthly ledger production
 * traffic uses, so the budget figures and the memory page's spend card are stale
 * the moment it returns. Leaving them cached would let an operator run three
 * paid queries in a row while the page kept showing the spend from before the
 * first one.
 */
export function useQueryLab() {
  const { session } = useSession();
  const queryClient = useQueryClient();
  return useMutation<QueryLabResult, Error, QueryLabRequest>({
    mutationFn: async (request) =>
      apiRequest<QueryLabResult>('/api/query-lab/query', {
        method: 'POST',
        body: request,
        csrfToken: session?.csrfToken,
      }),
    onSuccess: async (_result, request) => {
      if (request.mode === 'lexical') return;
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['budget', request.guildId] }),
        queryClient.invalidateQueries({ queryKey: ['memory', request.guildId] }),
      ]);
    },
  });
}
